/* eslint-disable react-refresh/only-export-components -- helpers module: utility functions co-located with memoized row components */
/**
 * ActionTable.helpers — extracted helpers and memoized row components for ActionTable.
 *
 * Row/cell/column primitives come from the shared component library
 * (`@/lib/base/table`) — this module only owns the action-specific wiring
 * (status icons, avatars, property cells, expanded-row content).
 */

import { ChevronDown, ChevronRight } from '@untitledui/icons';
import type React from 'react';
import { memo, useCallback } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { CheckboxBase } from '@/lib/base/forms/checkbox';
import { Table } from '@/lib/base/table';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { renderStatusIcon } from '@/lib/proposals/utils/actionStatus';
import { isActionAlreadyExecuted } from '@/lib/proposals/utils/partialExecution';
import { unwrapPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isObjectCreatedPayload } from '../../lib/proposals/core/types';
import { useActionDisplayNumber } from '../../lib/proposals/hooks/ActionDisplayNumberContext';
import type { SelectionState } from '../../lib/proposals/types/selection';
import {
  getAlreadyExecutedBadgeText,
  getSkippedBadgeText,
} from '../../lib/proposals/utils/actionWarnings';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import {
  ACTION_TABLE_AVATAR_CELL_CLASSES,
  ACTION_TABLE_CHROME_CELL_CLASSES,
  ACTION_TABLE_DATA_CELL_CLASSES,
} from './ActionTable.cellClasses';
import type { ColumnDef } from './ActionTable.types';
import ActionVersionTimeline from './ActionVersionTimeline';
import { CheckboxCell } from './CheckboxCell';
import { ContactRowAvatar } from './ContactRowAvatar';
import { mergeActionUpdate } from './multiActionTimeline.utils';

// ─── Helper functions ────────────────────────────────────────────────────────

export function formatPropertyName(key: string): string {
  return key
    .replace(/^ahoy_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function extractContactInfo(action: ProposedAction): {
  email: string | null;
  name: string;
} {
  if (!isObjectCreatedPayload(action.payload)) {
    return { email: null, name: '' };
  }

  const { properties } = action.payload.payload;

  let email: string | null = null;
  const rawEmail = properties['ahoy_email'];
  if (rawEmail) {
    const v = unwrapPropertyValue(rawEmail);
    if (Array.isArray(v)) {
      email = v[0] ?? null;
    } else if (typeof v === 'string') {
      email = v;
    } else {
      email = null;
    }
  }

  const getName = (k: string): string => {
    const raw = properties[k];
    if (!raw) {
      return '';
    }
    return String(unwrapPropertyValue(raw) ?? '');
  };

  const firstName = getName('ahoy_first_name');
  const lastName = getName('ahoy_last_name');
  return { email, name: [firstName, lastName].filter(Boolean).join(' ') };
}

export function extractPropertyValue(
  action: ProposedAction,
  key: string
): unknown {
  const wrapper = isObjectCreatedPayload(action.payload)
    ? action.payload.payload.properties[key]
    : undefined;
  return unwrapPropertyValue(wrapper);
}

// ─── Shared cell styling ────────────────────────────────────────────────────

// ─── Memoized row components ─────────────────────────────────────────────────

export interface ActionDataRowProps {
  action: ProposedAction;
  isSelected: boolean;
  isDisabled: boolean;
  isExpanded: boolean;
  hasEmailProperty: boolean;
  hasSelectionState: boolean;
  /**
   * Ordered columns — mixes property and association columns. Association
   * columns render "—" for non-hidden rows because the action payload doesn't
   * carry CRM associations (those land as sibling actions or on the
   * associated entity, not on this action).
   */
  columns: ColumnDef[];
  effectiveSchemaMap: Record<string, PropertyTypeSchema | undefined>;
  onToggle: (actionId: number) => void;
  onRowClick: (actionId: number) => void;
}

export const ActionDataRow = memo(function ActionDataRow({
  action,
  isSelected,
  isDisabled,
  isExpanded,
  hasEmailProperty,
  hasSelectionState,
  columns,
  effectiveSchemaMap,
  onToggle,
  onRowClick,
}: ActionDataRowProps) {
  // Row position in the rendered list — what "Select action N" must say.
  const displayNumber = useActionDisplayNumber(action.id);

  const handleAction = useCallback(
    () => onRowClick(getStableActionKey(action)),
    [onRowClick, action]
  );
  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // React Aria fires onAction for Enter/click but not for Space (which is
      // reserved for selection). Manually trigger the expand handler on Space
      // so keyboard-only users can toggle row expansion the same way as Enter.
      if (event.key === ' ') {
        event.preventDefault();
        handleAction();
      }
    },
    [handleAction]
  );

  const { email, name } = extractContactInfo(action);

  const isAlreadyRan = isActionAlreadyExecuted(action);

  return (
    // react-aria treats `id` as the collection key and THROWS ("Cannot change
    // the id of an item") if it moves on an existing node — so it must be the
    // version-stable identity, matching the React key on the row.
    <Table.Row
      id={String(getStableActionKey(action))}
      className={cx(
        'cursor-pointer',
        !isSelected && 'opacity-50 grayscale',
        isExpanded && 'bg-secondary'
      )}
      onAction={handleAction}
      onKeyDown={handleRowKeyDown}
    >
      <Table.Cell className={ACTION_TABLE_CHROME_CELL_CLASSES}>
        {isExpanded ? (
          <ChevronDown className='size-4 text-quaternary' />
        ) : (
          <ChevronRight className='size-4 text-quaternary' />
        )}
      </Table.Cell>
      {hasSelectionState && (
        <Table.Cell className={ACTION_TABLE_CHROME_CELL_CLASSES}>
          <CheckboxCell
            isSelected={isSelected}
            isDisabled={isDisabled}
            onToggle={onToggle}
            actionId={action.id}
            executionOrder={displayNumber}
            tooltip={
              action.verdict?.status === 'not_visible'
                ? "Some of this action's details aren't visible to you."
                : undefined
            }
          />
        </Table.Cell>
      )}
      {hasEmailProperty && (
        <Table.Cell className={ACTION_TABLE_AVATAR_CELL_CLASSES}>
          <ContactRowAvatar email={email} name={name} />
        </Table.Cell>
      )}
      <Table.Cell className={ACTION_TABLE_CHROME_CELL_CLASSES}>
        {renderStatusIcon(action)}
      </Table.Cell>
      {columns.map((col, idx) => (
        <Table.Cell key={col.key} className={ACTION_TABLE_DATA_CELL_CLASSES}>
          <div className='flex items-center gap-2 min-w-0'>
            {col.kind === 'property' ? (
              <PropertyValueLabel
                value={extractPropertyValue(action, col.key)}
                propertyType={effectiveSchemaMap[col.key]}
                fontSize='sm'
                truncate
              />
            ) : (
              // Non-hidden create-action rows don't carry CRM associations
              // on the payload, so we render an em-dash placeholder rather
              // than leave the cell blank and have the row shift width.
              <span className='text-sm text-fg-tertiary'>—</span>
            )}
            {idx === 0 && hasSelectionState && isAlreadyRan && (
              <Badge color='gray' size='sm'>
                {getAlreadyExecutedBadgeText(action)}
              </Badge>
            )}
            {idx === 0 && hasSelectionState && !isSelected && !isAlreadyRan && (
              <Badge color='gray' size='sm'>
                {getSkippedBadgeText(action)}
              </Badge>
            )}
          </div>
        </Table.Cell>
      ))}
    </Table.Row>
  );
});

// ─── Expanded row ───────────────────────────────────────────────────────────

export interface ActionExpandedRowProps {
  action: ProposedAction;
  totalColumns: number;
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  isProcessing: boolean;
}

export const ActionExpandedRow = memo(function ActionExpandedRow({
  action,
  totalColumns,
  proposal,
  onUpdate,
  isProcessing,
}: ActionExpandedRowProps) {
  const handleExpandedUpdate = useCallback(
    (updatedSingleActionProposal: ProposalDetail) => {
      const updatedAction = updatedSingleActionProposal.actions[0];
      if (!updatedAction) {
        return;
      }
      onUpdate(mergeActionUpdate(proposal, updatedAction));
    },
    [proposal, onUpdate]
  );

  return (
    // Must match the row's React key and the data row's collection id:
    // react-aria throws "Cannot change the id of an item" if a mounted node's
    // id moves, and a save mints a new action.id.
    <Table.ExpandedRow
      id={`${getStableActionKey(action)}-expanded`}
      colSpan={totalColumns}
    >
      <div
        data-component='action-detail'
        data-instance={String(action.id)}
        data-testid={`action-detail-${action.id}`}
        className='max-w-md'
      >
        <ActionVersionTimeline
          action={{ ...proposal, actions: [action] }}
          onUpdate={handleExpandedUpdate}
          isProcessing={isProcessing}
        />
      </div>
    </Table.ExpandedRow>
  );
});

// ─── Column headers ─────────────────────────────────────────────────────────

export interface ActionTableColumnHeadersProps {
  selectionState?: SelectionState;
  hasEmailProperty: boolean;
  columns: ColumnDef[];
  /**
   * Combined label map keyed by column key — property labels from
   * usePropertySchema and association labels from useAssociationTypes.
   */
  columnLabels?: Record<string, string>;
  onHeaderCheckboxChange: () => void;
}

export const ActionTableColumnHeaders = memo(function ActionTableColumnHeaders({
  selectionState,
  hasEmailProperty,
  columns,
  columnLabels,
  onHeaderCheckboxChange,
}: ActionTableColumnHeadersProps) {
  const handleHeaderCheckboxKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
        e.preventDefault();
        onHeaderCheckboxChange();
      }
    },
    [onHeaderCheckboxChange]
  );
  const handleHeaderCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onHeaderCheckboxChange();
    },
    [onHeaderCheckboxChange]
  );

  return (
    <Table.Header>
      <Table.Head id='expand' className={ACTION_TABLE_CHROME_CELL_CLASSES} />
      {selectionState && (
        <Table.Head id='checkbox' className={ACTION_TABLE_CHROME_CELL_CLASSES}>
          <button
            type='button'
            onClick={handleHeaderCheckboxClick}
            onKeyDown={handleHeaderCheckboxKeyDown}
            aria-label='Select all actions'
            className='flex items-center'
          >
            <CheckboxBase
              isSelected={selectionState.isAllSelected}
              isIndeterminate={
                selectionState.isSomeSelected && !selectionState.isAllSelected
              }
            />
          </button>
        </Table.Head>
      )}
      {hasEmailProperty && (
        <Table.Head id='avatar' className={ACTION_TABLE_AVATAR_CELL_CLASSES} />
      )}
      <Table.Head id='status' className={ACTION_TABLE_CHROME_CELL_CLASSES} />
      {columns.map((col, index) => (
        <Table.Head
          key={col.key}
          id={col.key}
          label={columnLabels?.[col.key] ?? formatPropertyName(col.key)}
          isRowHeader={index === 0}
          className='max-w-[200px]'
        />
      ))}
    </Table.Header>
  );
});
